import {
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form944DueDates = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 944 filing due date for tax year 2025 | TaxZerone ®"
          description="Eligible businesses need to file Form 944 before February 02, 2026, to avoid IRS penalties. Learn more about the deadline and IRS penalties for filing Form 944"
          keywords=""
          canonical="resources/employment-tax/form-944-due-dates/"
          ogType="article"
          jsonLdPricing="6.99"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="54869"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 944 </span>
          </span>{' '}
          Due Dates
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Employers, use Form 944 to report taxes withheld from your employees
          to the IRS.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944duedates`}
          >
            E-File Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/employment-tax/form-944-due-dates/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-06-15T09:00:00-05:00"
                      >
                        June 15, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-06-15T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        June 15, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Nikhil Chowdary
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 944 Due Date at a Glance
                  </Typography>
                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-944-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 944
                        </Link>
                        {
                          ' (Employer’s Annual Federal Tax Return) is used by eligible small employers to report federal income tax withheld from employees’ wages and the employer’s and employees’ Social Security and Medicare taxes.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Who Must File:</b>
                        {' Employers whose annual employment tax liability is '}
                        <b>less than $1,000</b>
                        {' and who are instructed by the IRS to '}
                        <Link
                          href="https://www.taxzerone.com/employment-tax/e-file-irs-form-944/"
                          sx={{ color: '#0202ff' }}
                        >
                          file Form 944
                        </Link>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Filing Frequency:</b>
                        {' Form 944 is filed '}
                        <b>annually</b>
                        {', instead of quarterly like '}
                        <Link
                          href="https://www.taxzerone.com/resources/employment-tax/form-941-instructions/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 941
                        </Link>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Filing Deadline:</b>
                        {' For the '}
                        <b>2025 tax year</b>
                        {', Form 944 is due '}
                        <b>February 2, 2026</b>
                        {
                          '. If all taxes were deposited on time and in full, the return may generally be filed by '
                        }
                        <b>February 10, 2026</b>.
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Information Required:</b>
                        {
                          ' Employers need their business name, trade name, EIN, address, wages, tips, other compensation, and applicable federal income, Social Security, and Medicare tax amounts.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        <b>Filing Methods:</b>
                        {' Form 944 can be filed '}
                        <b>electronically or by paper</b>
                        {'. E-filing offers a convenient way to '}
                        submit
                        {' the return and receive faster processing.'}
                      </span>
                    </li>
                  </Box>
                </Box>
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box />
                  {/* <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  ></Box> */}
                </Stack>
                {/* <Box
                  sx={{
                    pt: 0,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#where" sx={{ color: '#0202ff' }}>
                          Where to File Form 943?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#taxes" sx={{ color: '#0202ff' }}>
                          Depositing Your Taxes
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#interest"
                          id="what"
                          sx={{ color: '#0202ff' }}
                        >
                          Penalties and Interest?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 944?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  {' '}
                  <Link
                    href="/resources/employment-tax/form-944-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 944
                  </Link>
                  , is the annual tax form for employers to report the tax
                  withheld from the employees from the wages paid. This form is
                  used only when the total tax withheld from the employees is
                  less than $1000.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important dates in filing form 944
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Employers need to file the payroll tax form 944 with the IRS
                  if the tax liability for the tax year is below $1000 before
                  the deadline.
                </Typography>
                <Box className="deadline-dates" sx={{}}>
                  <Typography
                    variant="body1"
                    sx={{ p: 2, color: '#fff', fontSize: 18, fontWeight: 500 }}
                  >
                    <b style={{ fontSize: 22 }}>
                      {' '}
                      Deadline for the Tax Year 2025 :
                    </b>{' '}
                    February 02, 2026
                  </Typography>
                </Box>
                <Alert severity="warning" icon={false} sx={{ mb: 4, mt: 3 }}>
                  <b>Note:</b> If you paid the full taxes for the year on time,
                  you can file the return by February 10, 2026.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What are the details required?
                </Typography>
                <Typography variant="body1">
                  Details required to file Form 944:
                </Typography>

                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    Business name, Trade name, EIN, address of the business
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    Wages, tips, and other compensation
                  </li>
                  <li style={{ marginBottom: 12 }}>Total tax withheld</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Other compensation includes Medicare wages, tips, and Social
                  Security wages paid to the employees. Taxes include taxes
                  withheld from wages, tips, Medicare, and Social Security
                  wages.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing Method:
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b>E-Filing:</b> The IRS encourages employers to file form
                    944, Employer’s Annual Federal Tax Return using the
                    Electronic filing method for easy filing and faster
                    processing.
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b>Paper filing:</b> Employers can use the paper filing
                    method to file the tax return. Enter the mentioned details
                    in the form and send it to the IRS-mentioned address based
                    on the location.
                  </li>
                </ul>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent={{ xs: 'center', md: 'space-between' }}
                  alignItems="center"
                  spacing={2}
                  sx={{
                    py: 2,
                    px: { xs: 1, md: 2 },
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    border: '1px solid #ccc',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      textAlign: { xs: 'center', md: 'left' },
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        fontSize: '18px !important',
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      TaxZerone suggests businesses file returns using the
                      E-Filing method for a quick and easy process.
                    </Typography>
                  </Box>
                  <Box sx={{ width: 180 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944duedates`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Stack>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalties for late deposit and late filing
                </Typography>
                <ul style={{ marginBottom: 20 }}>
                  <li style={{ marginBottom: 12 }}>
                    <b> Failure-to-File:</b> If you fail to{' '}
                    <Link
                      href="/employment-tax/e-file-irs-form-944/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file Form 944
                    </Link>
                    , the penalty will be 5 to 25% of the tax due
                  </li>
                  <li style={{ marginBottom: 12 }}>
                    <b> Failure to Pay:</b> If you fail to pay the tax amount
                    within the deadline, the penalty can be up to 25% tax due
                  </li>
                </ul>
                <Alert severity="warning" icon={false} sx={{ mb: 4 }}>
                  <b>Note:</b> The Tax due and penalty will accumulate interest
                  until it is paid in full to the IRS
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Avoid IRS penalties, File your form 944 today with
                        TaxZerone
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 130, textAlign: 'center' }}
                      id="common"
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=944duedates`}
                      >
                        File Today
                      </Button>
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      {/* <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 940 within a few minutes using TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Choose a simplified tax filing process. Choose TaxZerone.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=940duedates`}
              >
                E-file Form 940
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container> */}
    </AppLayout>
  );
};

export default Form944DueDates;
